import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

export default function Auth({ mode }) {
  const isSignup = mode === "signup";
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = isSignup ? "/auth/register" : "/auth/login";
      const payload = isSignup ? form : { email: form.email, password: form.password };
      const res = await api.post(endpoint, payload);
      loginWithToken(res.data.token, res.data.user);
      toast.success(isSignup ? "Account created" : "Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 relative overflow-hidden border-r border-white/10">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10"><Logo size="lg" /></div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.1]">
            Build software at the <span className="gradient-text">speed of thought</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-md leading-relaxed">
            An entire AI engineering team that plans, designs, builds, tests, debugs and deploys — from a single sentence.
          </p>
          <div className="flex gap-3 flex-wrap">
            {["Plan", "Design", "Build", "Test", "Deploy"].map((s) => (
              <span key={s} className="font-mono text-xs px-3 py-1.5 rounded-full glass text-indigo-300">{s}</span>
            ))}
          </div>
        </div>
        <div className="relative z-10 font-mono text-xs text-slate-500">© 2026 NEXORA AI · v2.4</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">
            {isSignup ? "Create your account" : "Welcome back"}
          </h2>
          <p className="text-slate-400 mb-8">
            {isSignup ? "Start building for free. No credit card required." : "Sign in to continue building."}
          </p>

          <button
            data-testid="google-auth-button"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors font-medium mb-5"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-xs text-slate-500">OR</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <Field icon={User} testid="name-input" placeholder="Full name" value={form.name}
                onChange={(v) => setForm({ ...form, name: v })} />
            )}
            <Field icon={Mail} testid="email-input" type="email" placeholder="you@company.com" value={form.email}
              onChange={(v) => setForm({ ...form, email: v })} />
            <Field icon={Lock} testid="password-input" type="password" placeholder="Password" value={form.password}
              onChange={(v) => setForm({ ...form, password: v })} />
            <button
              data-testid="auth-submit-button"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 transition-colors font-semibold disabled:opacity-50"
            >
              {busy ? "Please wait…" : isSignup ? "Create account" : "Sign in"}
              {!busy && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <p className="text-slate-400 text-sm mt-6 text-center">
            {isSignup ? "Already have an account? " : "New to NEXORA? "}
            <Link to={isSignup ? "/login" : "/signup"} className="text-indigo-400 hover:text-indigo-300 font-medium">
              {isSignup ? "Sign in" : "Create one free"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input
        data-testid={testid}
        type={type}
        required
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 focus:border-indigo-500/60 focus:bg-white/[0.05] outline-none transition-colors placeholder:text-slate-600"
      />
    </div>
  );
}
